import type { LegacyColumnDef } from '@tanstack/react-table/legacy';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import Maybe from 'true-myth/maybe';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageKey } from '~/shared/internationalization';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { IconDelete, IconFileDownload, IconWarning } from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { TableActionButton } from '~/ui/core/table/Table';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { formatDateWithShortMonthByLocale } from '../../../../date/date-formatter';
import type { AttentionStatus, DocumentStatus, UploadedDocument } from './types';
import { documentNeedsAttention, isDocumentStatus } from './types';

const columnHelper = legacyCreateColumnHelper<UploadedDocument>();

const STATUS_TRANSLATION_KEYS: Record<Exclude<DocumentStatus, AttentionStatus>, TranslationKey> = {
    DELETED: 'pages.job-architecture-v2.knowledge-base.status.DELETED',
    ERROR: 'pages.job-architecture-v2.knowledge-base.status.ERROR',
    INDEXED: 'pages.job-architecture-v2.knowledge-base.status.INDEXED',
    PROCESSING: 'pages.job-architecture-v2.knowledge-base.status.PROCESSING',
    UPLOAD_FAILED: 'pages.job-architecture-v2.knowledge-base.status.UPLOAD_FAILED',
    UPLOADING: 'pages.job-architecture-v2.knowledge-base.status.UPLOADING',
};

const ATTENTION_MESSAGE_KEYS: Record<AttentionStatus, TranslationKey> = {
    COMPLEX: 'pages.job-architecture-v2.knowledge-base.attention-message.document-too-complex',
    PROTECTED: 'pages.job-architecture-v2.knowledge-base.attention-message.document-protected',
    UNSUPPORTED: 'pages.job-architecture-v2.knowledge-base.attention-message.unsupported-format',
};

const getStatusVariant = (status: string): 'error' | 'success' | 'warning' => {
    switch (status) {
        case 'ERROR':
            return 'error';
        case 'INDEXED':
        case 'PROCESSING':
            return 'success';
        default:
            return 'warning';
    }
};

export const DocumentStatusBadge: FunctionComponent<{ readonly status: string }> = ({ status }) => {
    const translate = useTranslate();

    if (status === 'UPLOADING') {
        return <Spinner />;
    }

    if (documentNeedsAttention(status)) {
        const messageKey = ATTENTION_MESSAGE_KEYS[status];

        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <Badge aria-label={translate(messageKey)} className="!gap-s !px-m" tabIndex={0} variant="warning">
                        <IconWarning />
                        {translate('pages.job-architecture-v2.knowledge-base.action-needed')}
                    </Badge>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top">{translate(messageKey)}</Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    const displayStatus = isDocumentStatus(status) && !documentNeedsAttention(status) ? status : 'ERROR';

    return <Badge variant={getStatusVariant(displayStatus)}>{translate(STATUS_TRANSLATION_KEYS[displayStatus])}</Badge>;
};

const ActionsCell: FunctionComponent<{
    readonly id: string;
    readonly name: string;
    readonly onDelete: (id: string, name: string) => void;
    readonly onDownload: (id: string) => void;
}> = ({ id, name, onDelete, onDownload }) => {
    const translate = useTranslate();

    return (
        <div className="flex w-full items-center justify-end">
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <TableActionButton
                        aria-label={translate('common.download')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload(id);
                        }}
                    >
                        <IconFileDownload size={16} />
                    </TableActionButton>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top">{translate('common.download')}</Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <TableActionButton
                        aria-label={translate('common.delete')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(id, name);
                        }}
                    >
                        <IconDelete size={16} />
                    </TableActionButton>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top">{translate('common.delete')}</Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        </div>
    );
};

const createCheckboxColumn = (maxSelectedDocuments: number): LegacyColumnDef<UploadedDocument> =>
    columnHelper.display({
        id: 'checkbox',
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onCheckedChange={(checked) => row.toggleSelected(checked === true)}
            />
        ),
        enableHiding: false,
        header: ({ table }) => {
            const currentRows = table.getRowModel().rows;
            const selectedRowCount = currentRows.filter((row) => row.getIsSelected()).length;
            const selectableRowCount = Math.min(currentRows.length, maxSelectedDocuments);

            return (
                <Checkbox
                    checked={selectableRowCount > 0 && selectedRowCount === selectableRowCount}
                    onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
                />
            );
        },
    });

const createDocumentColumn = (translate: Translate): LegacyColumnDef<UploadedDocument> =>
    columnHelper.accessor('name', {
        id: 'name',
        cell: ({ getValue }) => getValue(),
        enableHiding: false,
        header: translate('common.document'),
    });

const createStatusColumn = (translate: Translate): LegacyColumnDef<UploadedDocument> =>
    columnHelper.accessor('status', {
        id: 'status',
        cell: ({ row }) => <DocumentStatusBadge status={row.original.status} />,
        header: translate('common.status'),
    });

const createLastModifiedColumn = (translate: Translate, locale: LanguageKey): LegacyColumnDef<UploadedDocument> =>
    columnHelper.accessor('updatedAt', {
        id: 'updatedAt',
        cell: ({ getValue }) =>
            formatDateWithShortMonthByLocale({
                dateStringToFormat: Maybe.of(getValue()),
                locale,
            }).unwrapOr(''),
        header: translate('common.last-modified'),
    });

const createActionsColumn = (
    translate: Translate,
    onDelete: (id: string, name: string) => void,
    onDownload: (id: string) => void,
): LegacyColumnDef<UploadedDocument> =>
    columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
            <ActionsCell id={row.original.id} name={row.original.name} onDelete={onDelete} onDownload={onDownload} />
        ),
        enableHiding: false,
        header: () => <span className="w-full text-right">{translate('common.actions')}</span>,
    });

type UseColumnsOptions = {
    readonly locale: LanguageKey;
    readonly maxSelectedDocuments: number;
    readonly onDelete: (id: string, name: string) => void;
    readonly onDownload: (id: string) => void;
    readonly translate: Translate;
};

export const useColumns = ({ locale, maxSelectedDocuments, onDelete, onDownload, translate }: UseColumnsOptions) =>
    // biome-ignore lint/correctness/useExhaustiveDependencies: callbacks are intentionally excluded; TanStack Table requires stable column references to propagate updates through its internal memo chain.
    useMemo(
        () => [
            createCheckboxColumn(maxSelectedDocuments),
            createDocumentColumn(translate),
            createStatusColumn(translate),
            createLastModifiedColumn(translate, locale),
            createActionsColumn(translate, onDelete, onDownload),
        ],
        [locale, maxSelectedDocuments, translate],
    );
